import { ensurePresent } from "./utils";
import { TCF_CONSENT_IDS, TCF_VENDOR_ID } from "./config";
import { type Logger } from "./logger";
import { type TCFApi, type DqTagConsentPayload, type TCData } from "./models";

declare global {
  interface Window {
    __tcfapi?: TCFApi;
  }
}

export class ConsentProcessor {
  private _disabled: boolean;
  private _tcfData: TCData | null;
  private _loaded = false;
  private _tcfVendorId!: number;
  private _tcfPurposeIds!: number[];
  private _pendingConsentCallbacks: Array<
    (() => void) | (() => Promise<void>)
  > = [];
  private _loadedPromise: Promise<void>;
  private _loadedResolve!: () => void;

  constructor(private _logger: Logger) {
    this._disabled = false;
    this._tcfData = null;
    this._loadedPromise = new Promise<void>((resolve) => {
      this._loadedResolve = resolve;
    });
  }

  set disabled(value: boolean) {
    this._disabled = value;
  }

  get disabled(): boolean {
    return this._disabled;
  }

  get applied(): boolean {
    return Boolean(this._tcfData?.gdprApplies);
  }

  get loaded(): boolean {
    return this._loaded;
  }

  get hasConsentOrNotApplied(): boolean {
    if (this.disabled || !this.applied) {
      return true;
    }
    const hasRequiredVendorConsents =
      this._tcfVendorId in (this._tcfData?.vendor?.consents ?? {});
    const hasRequiredPurposeConsents = this._tcfPurposeIds.every(
      (id) => this._tcfData?.purpose?.consents[id],
    );
    return hasRequiredPurposeConsents && hasRequiredVendorConsents;
  }

  get payload(): DqTagConsentPayload {
    return {
      disableTcf: this.disabled,
      tcf: this.disabled ? undefined : this._tcfData?.tcString,
      gdprApplies: this.disabled ? undefined : this.applied,
    };
  }

  public async runIfConsented(
    onConsented: (() => void) | (() => Promise<void>),
  ): Promise<void> {
    if (!this.disabled) {
      await this._loadedPromise;
    }

    if (this.disabled || !this.applied) {
      await onConsented();
      return;
    }

    if (this.hasConsentOrNotApplied) {
      await onConsented();
    } else {
      this._logger.printError(
        `Vendor consent denied for IDs: ${this._tcfPurposeIds
          .filter((id) => this._tcfData?.purpose?.consents[id] !== true)
          .join(", ")}`,
      );

      this._logger.printDebug(
        "Adding callback to pending queue for consent change",
      );
      this._pendingConsentCallbacks.push(onConsented);
    }
  }

  public async initialize(
    tcfVendorId: number = TCF_VENDOR_ID,
    tcfPurposeIds: number[] = TCF_CONSENT_IDS,
  ): Promise<void> {
    this._tcfVendorId = tcfVendorId;
    this._tcfPurposeIds = tcfPurposeIds;
    if (this.disabled) {
      return;
    }
    const tcf = await ensurePresent(() => window.__tcfapi as TCFApi, 5000);
    if (!tcf) {
      this._loaded = true;
      this._loadedResolve();
      this._logger.printError("TCF API is not available");
      return;
    }
    tcf("getTCData", 2, (tcData: TCData, success: boolean) => {
      this._logger.printDebug("TC data received:", tcData);
      if (
        tcData.eventStatus !== "tcloaded" &&
        tcData.eventStatus !== "useractioncomplete" &&
        tcData.eventStatus !== "tcstringupdated" &&
        tcData.gdprApplies
      ) {
        return;
      }
      this._tcfData = success ? tcData : null;
      if (!success) {
        this._logger.printError(
          "TCF data could not be loaded or is invalid",
          tcData,
        );
      }
      this._loaded = true;
      this._loadedResolve();
    });
    tcf("addEventListener", 2, (tcData: TCData, success: boolean) => {
      this._logger.printDebug("TC event received:", tcData);
      if (
        tcData.eventStatus !== "tcloaded" &&
        tcData.eventStatus !== "useractioncomplete" &&
        tcData.eventStatus !== "tcstringupdated"
      ) {
        return;
      }

      const previousConsentState = this.hasConsentOrNotApplied;

      this._tcfData = success ? tcData : null;
      if (!success) {
        this._logger.printError(
          "TCF data could not be loaded or is invalid",
          tcData,
        );
      }
      this._loaded = true;
      this._loadedResolve();

      if (
        tcData.eventStatus === "useractioncomplete" &&
        !previousConsentState &&
        this.hasConsentOrNotApplied
      ) {
        this._logger.printDebug(
          `User granted consent, executing ${this._pendingConsentCallbacks.length} pending callbacks`,
        );
        const callbacks = [...this._pendingConsentCallbacks];
        this._pendingConsentCallbacks = [];
        callbacks.forEach((callback) => {
          try {
            void callback();
          } catch (error) {
            this._logger.printError(
              "Error executing pending consent callback:",
              error,
            );
          }
        });
      }
    });
  }
}
